import React, { useState, useEffect, useCallback, useRef } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { 
  Calendar as CalendarIcon, Clock, ArrowLeftRight, Gift, Plus, X, FileEdit, 
  AlertTriangle, CheckCircle2, Eye, Download, Search, Filter, Settings, 
  User, ChevronLeft, ChevronRight, RefreshCw, ArrowRight, History, 
  UserCheck, MoreVertical, FileSpreadsheet, FileText, Trash2, Check,
  ChevronDown, UserMinus, ShieldAlert, HeartPulse, HelpCircle, Building2, TrendingUp
} from 'lucide-react';
const toast = {
  success: (msg) => alert(msg),
  error: (msg) => alert(msg),
  info: (msg) => alert(msg)
};

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ESSLeaveAttendance = () => {
  const [activeTab, setActiveTab] = useState('leave');
  
  // Core state from backend
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveApps, setLeaveApps] = useState([]);
  const [attendance, setAttendance] = useState({ records: [], summary: {} });
  const [shiftRequests, setShiftRequests] = useState([]);
  const [compoffRequests, setCompoffRequests] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [eligibleDates, setEligibleDates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // UI state
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewItem, setPreviewItem] = useState(null); // For timeline & preview drawer
  const [previewType, setPreviewType] = useState(null); // 'leave' | 'reg' | 'shift' | 'compoff'

  // Wizard state for Apply Leave
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveWizardStep, setLeaveWizardStep] = useState(1);
  const [leaveForm, setLeaveForm] = useState({
    leave_type_id: '',
    from_date: '',
    to_date: '',
    half_day: false,
    reason: '',
    emergency_contact: '',
    delegate_employee: '',
    attachment_name: ''
  });

  // Modal / Form states for other tabs
  const [showRegModal, setShowRegModal] = useState(false);
  const [regForm, setRegForm] = useState({
    attendance_date: '',
    request_type: 'missing_punch',
    proposed_in_time: '',
    proposed_out_time: '',
    alternate_status: '',
    reason: '',
    manager_id: '',
    attachment_name: ''
  });

  const [showShiftModal, setShowShiftModal] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    request_type: 'change',
    requested_date: '',
    current_shift_id: 'GEN_9_6',
    requested_shift_id: 'MOR_6_2',
    reason: '',
    comments: ''
  });

  const [showCompoffModal, setShowCompoffModal] = useState(false);
  const [compoffForm, setCompoffForm] = useState({
    worked_date: '',
    comp_off_date: '',
    hours_worked: 8,
    reason: '',
    attachment_name: ''
  });

  // Search / Filter / Sort / Pagination Common States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    leave: ['appNo', 'leaveType', 'dates', 'days', 'reason', 'status', 'actions'],
    reg: ['reqNo', 'date', 'oldTime', 'newTime', 'reason', 'status', 'actions'],
    shift: ['reqNo', 'shifts', 'effectiveDate', 'reason', 'status', 'actions'],
    compoff: ['reqNo', 'workedDate', 'offDate', 'hours', 'status', 'actions']
  });

  // Calendar View month state (separate from attendance month)
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bal, apps, sr, co, cos, regs, elig] = await Promise.all([
        axios.get(`${API_URL}/ess/leave/balances`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/leave/applications`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/shift-requests`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/compoff-requests`, { withCredentials: true }),
        axios.get(`${API_URL}/companies`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/regularization`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/regularization/eligible-dates?days=14`, { withCredentials: true }),
      ]);
      setLeaveBalances(bal.data || []);
      setLeaveApps(apps.data || []);
      setShiftRequests(sr.data || []);
      setCompoffRequests(co.data || []);
      setCompanies(cos.data || []);
      setRegularizations(regs.data || []);
      setEligibleDates(elig.data || []);

      if (cos.data?.length > 0) {
        const lt = await axios.get(`${API_URL}/leave/types?company_id=${cos.data[0].id}`, { withCredentials: true });
        setLeaveTypes(lt.data || []);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const fetchAttendance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/ess/attendance?month=${month}`, { withCredentials: true });
      setAttendance(data || { records: [], summary: {} });
    } catch (e) {
      console.error(e);
    }
  }, [month]);

  useEffect(() => {
    fetchAttendance();
  }, [month, fetchAttendance]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      const tabMap = {
        'leave': 'leave',
        'leaves': 'leave',
        'attendance': 'attendance',
        'regularization': 'regularization',
        'regularizations': 'regularization',
        'shift': 'shift',
        'shifts': 'shift',
        'compoff': 'compoff',
        'comp-off': 'compoff',
      };
      if (tabMap[tabParam.toLowerCase()]) {
        setActiveTab(tabMap[tabParam.toLowerCase()]);
      }
    }
  }, []);

  // Clean-up filters when active tab changes
  useEffect(() => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setCurrentPage(1);
  }, [activeTab]);

  // Status utility colors
  const statusColor = (s) => {
    const colors = {
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      pending_hr: 'bg-blue-50 text-blue-700 border-blue-200',
      pending_manager: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
      rework: 'bg-orange-50 text-orange-700 border-orange-200',
      cancelled: 'bg-slate-50 text-slate-500 border-slate-200'
    };
    return colors[s] || 'bg-slate-50 text-slate-600 border-slate-200';
  };

  // KEEP EXACT COLOR MAPPING FOR THE ATTENDANCE GRID CELLS
  const attColor = (s) => ({
    present: 'bg-emerald-400',
    absent: 'bg-red-400',
    leave: 'bg-blue-400',
    half_day: 'bg-amber-400',
    weekly_off: 'bg-slate-300',
    holiday: 'bg-purple-400'
  })[s] || 'bg-slate-200';

  // Submission Handlers
  const handleLeaveSubmit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const lt = leaveTypes.find(t => t.id === leaveForm.leave_type_id);
      // Calculate days count
      let computedDays = 1;
      if (leaveForm.from_date && leaveForm.to_date) {
        const d1 = new Date(leaveForm.from_date);
        const d2 = new Date(leaveForm.to_date);
        const diffTime = Math.abs(d2 - d1);
        computedDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      }
      if (leaveForm.half_day) computedDays = 0.5;

      const payload = {
        leave_type_id: leaveForm.leave_type_id,
        from_date: leaveForm.from_date,
        to_date: leaveForm.to_date,
        total_days: computedDays,
        days: computedDays,
        is_partial_day: !!leaveForm.half_day,
        half_day: !!leaveForm.half_day,
        reason: leaveForm.reason || 'N/A',
        contact_during_leave: leaveForm.emergency_contact || null,
        emergency_contact: leaveForm.emergency_contact || null,
        delegate_employee: leaveForm.delegate_employee || null,
        address_during_leave: leaveForm.delegate_employee || null,
        attachment_name: leaveForm.attachment_name || null,
        company_id: companies[0]?.id,
        leave_type_name: lt?.name || '',
      };

      await axios.post(`${API_URL}/leave/applications`, payload, { withCredentials: true });

      setShowLeaveModal(false);
      setLeaveWizardStep(1);
      setLeaveForm({
        leave_type_id: '', from_date: '', to_date: '', half_day: false,
        reason: '', emergency_contact: '', delegate_employee: '', attachment_name: ''
      });
      toast.success('Leave application submitted successfully');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error submitting leave');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeaveCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this leave application?')) return;
    try {
      // Simulate/call cancel leave application
      await axios.patch(`${API_URL}/leave/applications/${id}`, { status: 'cancelled' }, { withCredentials: true });
      toast.success('Leave application cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel leave');
    }
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    if (!regForm.attendance_date || !regForm.reason.trim()) {
      toast.error('Please pick a date and provide a reason.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/ess/regularization`, {
        ...regForm,
        proposed_in_time: regForm.proposed_in_time || null,
        proposed_out_time: regForm.proposed_out_time || null,
        alternate_status: regForm.alternate_status || null,
      }, { withCredentials: true });

      setShowRegModal(false);
      setRegForm({
        attendance_date: '', request_type: 'missing_punch', proposed_in_time: '',
        proposed_out_time: '', alternate_status: '', reason: '', manager_id: '', attachment_name: ''
      });
      toast.success('Regularization request submitted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error submitting regularization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegCancel = async (id) => {
    if (!window.confirm('Cancel this regularization request?')) return;
    try {
      await axios.post(`${API_URL}/ess/regularization/${id}/cancel`, null, { withCredentials: true });
      toast.success('Regularization request cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleShiftSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/ess/shift-requests`, shiftForm, { withCredentials: true });
      setShowShiftModal(false);
      setShiftForm({
        request_type: 'change', requested_date: '', current_shift_id: 'GEN_9_6',
        requested_shift_id: 'MOR_6_2', reason: '', comments: ''
      });
      toast.success('Shift exchange request submitted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error submitting shift request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompoffSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/ess/compoff-requests`, compoffForm, { withCredentials: true });
      setShowCompoffModal(false);
      setCompoffForm({ worked_date: '', comp_off_date: '', hours_worked: 8, reason: '', attachment_name: '' });
      toast.success('Comp-Off request submitted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error submitting comp-off request');
    } finally {
      setSubmitting(false);
    }
  };

  // Excel and PDF Export simulations
  const handleExportExcel = (type) => {
    toast.success(`Exporting ${type} History to Excel...`);
  };

  const handleExportPDF = (type) => {
    window.print();
  };

  // Helper for sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Dynamic calculations for Leave summary cards
  const clData = leaveBalances.find(b => b.leave_type_code === 'CL') || { balance: 0, entitled: 12, availed: 0 };
  const slData = leaveBalances.find(b => b.leave_type_code === 'SL') || { balance: 0, entitled: 12, availed: 0 };
  const elData = leaveBalances.find(b => b.leave_type_code === 'EL') || { balance: 0, entitled: 21, availed: 0 };
  const coData = leaveBalances.find(b => b.leave_type_code === 'CO') || { balance: 0, entitled: 0, availed: 0 };

  const totalLeaveEntitled = (clData.entitled || 0) + (slData.entitled || 0) + (elData.entitled || 0);
  const totalLeaveAvailed = (clData.availed || 0) + (slData.availed || 0) + (elData.availed || 0);
  const totalLeaveBalance = (clData.balance || 0) + (slData.balance || 0) + (elData.balance || 0);

  const appliedCount = leaveApps.length;
  const approvedCount = leaveApps.filter(a => a.status === 'approved').length;
  const pendingCount = leaveApps.filter(a => ['pending', 'pending_hr', 'pending_manager'].includes(a.status)).length;
  const rejectedCount = leaveApps.filter(a => a.status === 'rejected').length;

  // Regularization card counts
  const pendingRegCount = regularizations.filter(r => r.status === 'pending').length;
  const approvedRegCount = regularizations.filter(r => r.status === 'approved').length;
  const rejectedRegCount = regularizations.filter(r => r.status === 'rejected').length;
  const currentMonthRegCount = regularizations.filter(r => r.attendance_date?.startsWith(new Date().toISOString().slice(0, 7))).length;

  // Shift Request card counts
  const pendingShiftCount = shiftRequests.filter(s => s.status === 'pending').length;
  const approvedShiftCount = shiftRequests.filter(s => s.status === 'approved').length;
  const rejectedShiftCount = shiftRequests.filter(s => s.status === 'rejected').length;

  // Comp-off counts
  const availableCompoff = compoffRequests.filter(c => c.status === 'approved').length - compoffRequests.filter(c => c.status === 'availed').length;
  const usedCompoff = compoffRequests.filter(c => c.status === 'availed').length;
  const pendingCompoff = compoffRequests.filter(c => c.status === 'pending').length;

  // Tab configurations
  const tabs = [
    { key: 'leave', label: 'Leave', icon: CalendarIcon },
    { key: 'attendance', label: 'Attendance', icon: Clock },
    { key: 'regularization', label: 'Regularization', icon: FileEdit },
    { key: 'shift', label: 'Shift Requests', icon: ArrowLeftRight },
    { key: 'compoff', label: 'Comp-Off', icon: Gift },
  ];

  // Helper for Leave Interactive Calendar
  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const monthIndex = calendarMonth.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const startCol = (firstDay.getDay() + 6) % 7; // Mon=0
    const daysInMonth = lastDay.getDate();

    const cells = [];
    // Pad
    for (let i = 0; i < startCol; i++) {
      cells.push(null);
    }
    // Days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(year, monthIndex, d));
    }
    return cells;
  };

  const getCalendarDayStatus = (dateObj) => {
    if (!dateObj) return { type: 'empty' };
    const iso = dateObj.toISOString().slice(0, 10);
    
    // Find matching leave application
    const matchApp = leaveApps.find(app => {
      return iso >= app.from_date && iso <= app.to_date;
    });

    if (matchApp) {
      if (matchApp.status === 'approved') return { type: 'leave_approved', details: matchApp };
      if (matchApp.status === 'rejected') return { type: 'leave_rejected', details: matchApp };
      return { type: 'leave_pending', details: matchApp };
    }

    // Weekend (Sat/Sun)
    const day = dateObj.getDay();
    if (day === 0 || day === 6) {
      return { type: 'weekend' };
    }

    return { type: 'weekday' };
  };

  return (
    <MainLayout>
      <div data-testid="ess-leave-attendance-page" className="space-y-6 max-w-6xl pb-16">
        
        {/* Sticky Header */}
        <div className="sticky top-0 bg-slate-50/90 backdrop-blur-md z-20 py-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Leave & Attendance</h1>
            <p className="text-sm text-slate-500 mt-1">Manage leave balances, request punch regularizations, view calendar and shifts.</p>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchAll} 
              className="p-2 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors"
              title="Refresh Data"
            >
              <RefreshCw className="w-4 h-4 text-slate-600" />
            </button>
            
            {activeTab === 'leave' && (
              <button 
                data-testid="apply-leave-btn" 
                onClick={() => { setShowLeaveModal(true); setLeaveWizardStep(1); }} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Apply Leave</span>
              </button>
            )}

            {activeTab === 'regularization' && (
              <button 
                data-testid="new-reg-btn" 
                onClick={() => setShowRegModal(true)} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>New Request</span>
              </button>
            )}

            {activeTab === 'shift' && (
              <button 
                data-testid="new-shift-btn" 
                onClick={() => setShowShiftModal(true)} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>New Shift Request</span>
              </button>
            )}

            {activeTab === 'compoff' && (
              <button 
                data-testid="new-compoff-btn" 
                onClick={() => setShowCompoffModal(true)} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>New Comp-Off Request</span>
              </button>
            )}
          </div>
        </div>

        {/* Tab Bar navigation */}
        <div className="flex space-x-1 bg-slate-100 rounded-xl p-1 overflow-x-auto border border-slate-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button 
                key={tab.key} 
                data-testid={`tab-${tab.key}`} 
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 py-2.5 px-5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.key 
                    ? 'bg-white text-[#0052FF] shadow-sm font-semibold border-b-2 border-b-[#0052FF]' 
                    : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          // Loading skeleton state
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 animate-pulse space-y-3">
                  <div className="h-4 bg-slate-100 rounded w-1/2"></div>
                  <div className="h-8 bg-slate-100 rounded w-1/3"></div>
                </div>
              ))}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 animate-pulse space-y-4">
              <div className="h-4 bg-slate-100 rounded w-1/4"></div>
              <div className="h-20 bg-slate-100 rounded w-full"></div>
            </div>
          </div>
        ) : (
          <>
            {/* 1. LEAVE TAB REDESIGN */}
            {activeTab === 'leave' && (
              <div className="space-y-6">
                
                {/* Dashboard Cards Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {/* Annual Leave */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-[#0052FF] transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Annual Leave</p>
                      <CalendarIcon className="w-4 h-4 text-[#0052FF]" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{totalLeaveBalance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {totalLeaveAvailed}</span>
                      <span>Total: {totalLeaveEntitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-[#0052FF] h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (totalLeaveBalance / Math.max(totalLeaveEntitled, 1)) * 100)}%` }} />
                    </div>
                  </div>

                  {/* Casual Leave */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-indigo-400 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Casual Leave</p>
                      <User className="w-4 h-4 text-indigo-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{clData.balance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {clData.availed}</span>
                      <span>Total: {clData.entitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (clData.balance / Math.max(clData.entitled, 1)) * 100)}%` }} />
                    </div>
                  </div>

                  {/* Sick Leave */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-emerald-400 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sick Leave</p>
                      <HeartPulse className="w-4 h-4 text-emerald-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{slData.balance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {slData.availed}</span>
                      <span>Total: {slData.entitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (slData.balance / Math.max(slData.entitled, 1)) * 100)}%` }} />
                    </div>
                  </div>

                  {/* Earned Leave */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-amber-400 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Earned Leave</p>
                      <TrendingUp className="w-4 h-4 text-amber-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{elData.balance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {elData.availed}</span>
                      <span>Total: {elData.entitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (elData.balance / Math.max(elData.entitled, 1)) * 100)}%` }} />
                    </div>
                  </div>

                  {/* Comp-Off Balance */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-purple-400 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Comp-Off Balance</p>
                      <Gift className="w-4 h-4 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{coData.balance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {coData.availed}</span>
                      <span>Total: {coData.entitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-purple-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (coData.balance / Math.max(coData.entitled, 1)) * 100)}%` }} />
                    </div>
                  </div>
                </div>

                {/* Second Row Counters */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-[#0052FF]/5 border border-[#0052FF]/10 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500">Applied</p>
                      <p className="text-xl font-bold text-[#0052FF] font-mono mt-1">{appliedCount}</p>
                    </div>
                    <CalendarIcon className="w-6 h-6 text-[#0052FF]/30" />
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-emerald-600">Approved</p>
                      <p className="text-xl font-bold text-emerald-700 font-mono mt-1">{approvedCount}</p>
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-emerald-300" />
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-amber-600">Pending</p>
                      <p className="text-xl font-bold text-amber-700 font-mono mt-1">{pendingCount}</p>
                    </div>
                    <Clock className="w-6 h-6 text-amber-300" />
                  </div>
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-red-600">Rejected</p>
                      <p className="text-xl font-bold text-red-700 font-mono mt-1">{rejectedCount}</p>
                    </div>
                    <X className="w-6 h-6 text-red-300" />
                  </div>
                </div>

                {/* Calendar & Filters section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  
                  {/* Calendar View */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5 lg:col-span-1 shadow-sm">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                      <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-[#0052FF]" />
                        Leave Calendar
                      </h3>
                      <div className="flex items-center gap-1.5">
                        <button 
                          onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          <ChevronLeft className="w-4 h-4 text-slate-600" />
                        </button>
                        <span className="text-xs font-semibold text-slate-700 font-mono">
                          {calendarMonth.toLocaleString('default', { month: 'short', year: 'numeric' })}
                        </span>
                        <button 
                          onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          <ChevronRight className="w-4 h-4 text-slate-600" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center mb-3">
                      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, idx) => (
                        <span key={idx} className="text-[10px] font-bold text-slate-400 uppercase py-1">{day}</span>
                      ))}
                      {getCalendarDays().map((dayDate, idx) => {
                        if (!dayDate) return <div key={`empty-${idx}`} />;
                        const statusInfo = getCalendarDayStatus(dayDate);
                        const isToday = new Date().toDateString() === dayDate.toDateString();

                        let colorClass = 'text-slate-700 hover:bg-slate-50 border-transparent';
                        if (statusInfo.type === 'leave_approved') colorClass = 'bg-emerald-50 border-emerald-200 text-emerald-700 font-semibold';
                        if (statusInfo.type === 'leave_pending') colorClass = 'bg-amber-50 border-amber-200 text-amber-700 font-semibold';
                        if (statusInfo.type === 'leave_rejected') colorClass = 'bg-red-50 border-red-200 text-red-700 font-semibold';
                        if (statusInfo.type === 'weekend') colorClass = 'bg-slate-50 text-slate-400 border-transparent';

                        return (
                          <button
                            key={idx}
                            onClick={() => {
                              if (statusInfo.details) {
                                setPreviewItem(statusInfo.details);
                                setPreviewType('leave');
                              } else {
                                toast.info(`${dayDate.toDateString()} is a ${statusInfo.type}`);
                              }
                            }}
                            className={`aspect-square w-full rounded-lg border text-xs flex flex-col items-center justify-center transition-all ${colorClass} ${
                              isToday ? 'ring-2 ring-[#0052FF] ring-offset-1' : ''
                            }`}
                          >
                            {dayDate.getDate()}
                          </button>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-2 text-[10px] text-slate-500 border-t border-slate-100 pt-3">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-400" />Approved</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-400" />Pending</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-400" />Rejected</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-slate-200" />Weekend</span>
                    </div>
                  </div>

                  {/* Leave History List/Table */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 p-3 rounded-xl shadow-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 text-slate-400 shrink-0" />
                        <input
                          type="text"
                          placeholder="Search applications..."
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                          className="w-full text-xs focus:outline-none bg-transparent"
                        />
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <select
                          value={statusFilter}
                          onChange={e => setStatusFilter(e.target.value)}
                          className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-slate-50"
                        >
                          <option value="all">All Status</option>
                          <option value="approved">Approved</option>
                          <option value="pending">Pending</option>
                          <option value="rejected">Rejected</option>
                          <option value="cancelled">Cancelled</option>
                        </select>

                        <button 
                          onClick={() => handleExportExcel('Leave')}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="hidden sm:inline">Excel</span>
                        </button>
                      </div>
                    </div>

                    {/* Table View */}
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                            <tr>
                              <th className="px-4 py-3 font-semibold">App No</th>
                              <th className="px-4 py-3 font-semibold">Leave Type</th>
                              <th className="px-4 py-3 font-semibold">Dates</th>
                              <th className="px-4 py-3 font-semibold text-center">Days</th>
                              <th className="px-4 py-3 font-semibold">Status</th>
                              <th className="px-4 py-3 font-semibold text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {leaveApps
                              .filter(a => {
                                if (statusFilter !== 'all' && a.status !== statusFilter) return false;
                                if (searchTerm && !`${a.leave_type_name} ${a.reason}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
                                return true;
                              })
                              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                              .map((app, idx) => (
                                <tr key={app.id || idx} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="px-4 py-3 font-mono font-medium text-slate-600">
                                    L-{String(idx + 1).padStart(4, '0')}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-slate-800">
                                    {app.leave_type_name || app.leave_type || 'Leave'}
                                  </td>
                                  <td className="px-4 py-3 text-slate-500">
                                    <span className="font-mono">{app.from_date}</span> to <span className="font-mono">{app.to_date}</span>
                                  </td>
                                  <td className="px-4 py-3 text-center font-semibold text-slate-700">
                                    {app.days || 1}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase ${statusColor(app.status)}`}>
                                      {app.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center justify-center gap-1.5">
                                      <button 
                                        onClick={() => { setPreviewItem(app); setPreviewType('leave'); }}
                                        className="p-1 hover:bg-slate-100 rounded text-slate-600"
                                        title="Preview Approval Timeline"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                      </button>
                                      <button 
                                        onClick={() => handleExportPDF('Leave')}
                                        className="p-1 hover:bg-slate-100 rounded text-slate-600"
                                        title="Download PDF"
                                      >
                                        <FileText className="w-3.5 h-3.5" />
                                      </button>
                                      {['pending', 'pending_manager', 'pending_hr'].includes(app.status) && (
                                        <button 
                                          onClick={() => handleLeaveCancel(app.id)}
                                          className="p-1 hover:bg-red-50 rounded text-red-600"
                                          title="Cancel Request"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            {leaveApps.length === 0 && (
                              <tr>
                                <td colSpan={6} className="text-center text-slate-400 py-10 font-medium">
                                  No leave applications found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {leaveApps.length > 0 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-[11px] text-slate-500">
                          <div>
                            Showing {Math.min(leaveApps.length, (currentPage - 1) * pageSize + 1)}-{Math.min(leaveApps.length, currentPage * pageSize)} of {leaveApps.length} applications
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(prev => prev - 1)}
                              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50 hover:bg-slate-50 font-medium transition-colors"
                            >
                              Prev
                            </button>
                            <button
                              disabled={currentPage * pageSize >= leaveApps.length}
                              onClick={() => setCurrentPage(prev => prev + 1)}
                              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50 hover:bg-slate-50 font-medium transition-colors"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* 2. ATTENDANCE TAB - KEEP EXACTLY AS ORIGINAL */}
            {activeTab === 'attendance' && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <input type="month" data-testid="att-month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span className="text-emerald-600 font-medium">Present: {attendance.summary?.present || 0}</span>
                    <span className="text-red-600 font-medium">Absent: {attendance.summary?.absent || 0}</span>
                    <span className="text-blue-600 font-medium">Leave: {attendance.summary?.leave || 0}</span>
                    <span className="text-amber-600 font-medium">Half-day: {attendance.summary?.half_day || 0}</span>
                    <span className="text-slate-500 font-medium">Weekly-off: {attendance.summary?.weekly_off || 0}</span>
                    <span className="text-purple-600 font-medium">Holiday: {attendance.summary?.holiday || 0}</span>
                    <span className="text-slate-700 font-semibold">Total: {attendance.summary?.total || 0}</span>
                  </div>
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-3 text-xs text-slate-600" data-testid="att-legend">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-400" />Present</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-400" />Absent</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-400" />Leave</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-400" />Half-day</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-slate-300" />Weekly Off</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-purple-400" />Holiday</span>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="att-grid">
                  <div className="grid grid-cols-7 gap-1 p-3">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-500 py-1 border-b border-slate-100">{d}</div>)}
                    {(() => {
                      if (!month) return null;
                      const [y, m] = month.split('-').map(Number);
                      if (!y || !m) return null;
                      const firstDay = new Date(y, m - 1, 1);
                      const daysInMonth = new Date(y, m, 0).getDate();
                      const startCol = (firstDay.getDay() + 6) % 7;
                      const byDate = {};
                      (attendance.records || []).forEach(r => { if (r.attendance_date) byDate[r.attendance_date] = r; });
                      const cells = [];
                      for (let i = 0; i < startCol; i++) {
                        cells.push(<div key={`pad-${i}`} />);
                      }
                      for (let d = 1; d <= daysInMonth; d++) {
                        const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        const r = byDate[iso];
                        const status = (r?.status || r?.attendance_status || '').toLowerCase();
                        const color = attColor(status);
                        const label = status ? status.replace('_', ' ') : 'no data';
                        cells.push(
                          <div key={iso} className="text-center py-1.5" data-testid={`att-cell-${iso}`} title={`${iso} — ${label}`}>
                            <div className={`w-9 h-9 mx-auto rounded-full flex items-center justify-center text-xs font-semibold ${r ? 'text-white' : 'text-slate-400 bg-slate-50 border border-slate-200'} ${r ? color : ''}`}>
                              {String(d).padStart(2, '0')}
                            </div>
                            {r?.in_time && (
                              <div className="text-[10px] text-slate-500 mt-0.5 leading-none font-mono">{r.in_time}</div>
                            )}
                          </div>
                        );
                      }
                      return cells;
                    })()}
                  </div>
                  {attendance.records.length === 0 && <p className="text-sm text-slate-400 py-6 text-center" data-testid="att-empty">No attendance records for this month</p>}
                </div>
              </div>
            )}

            {/* 3. REGULARIZATION TAB REDESIGN */}
            {activeTab === 'regularization' && (
              <div className="space-y-6" data-testid="regularization-tab">
                
                {/* Regularization Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Requests</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1 font-mono">{pendingRegCount}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Approved Requests</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1 font-mono">{approvedRegCount}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rejected Requests</p>
                    <p className="text-2xl font-bold text-red-600 mt-1 font-mono">{rejectedRegCount}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Current Month Total</p>
                    <p className="text-2xl font-bold text-[#0052FF] mt-1 font-mono">{currentMonthRegCount}</p>
                  </div>
                </div>

                {/* Eligible Dates Warning Banner */}
                {eligibleDates.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="reg-eligible-banner">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-900 flex-1">
                      <p className="font-semibold text-sm">Action Required: {eligibleDates.length} regularization-eligible day(s)</p>
                      <p className="mt-1">Punches are missing or incomplete for these dates in the last 14 days. Please file regularization requests.</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {eligibleDates.map((d, i) => (
                          <span key={i} className="px-2 py-1 bg-white border border-amber-200 rounded font-mono font-medium">
                            {d.attendance_date} ({d.current_status})
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Search & List */}
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 p-3 rounded-xl shadow-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      <Search className="w-4 h-4 text-slate-400 shrink-0" />
                      <input
                        type="text"
                        placeholder="Search corrections..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full text-xs focus:outline-none bg-transparent"
                      />
                    </div>
                    <button 
                      onClick={() => handleExportExcel('Regularization')}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Export</span>
                    </button>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Request No</th>
                            <th className="px-4 py-3 font-semibold">Date</th>
                            <th className="px-4 py-3 font-semibold">Old Punch Time</th>
                            <th className="px-4 py-3 font-semibold">New Proposed Time</th>
                            <th className="px-4 py-3 font-semibold">Reason</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3 font-semibold text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {regularizations
                            .filter(r => {
                              if (searchTerm && !`${r.attendance_date} ${r.reason}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
                              return true;
                            })
                            .map((r, i) => (
                              <tr key={r.id || i} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-4 py-3 font-mono font-medium text-slate-600">R-{String(i + 1).padStart(4, '0')}</td>
                                <td className="px-4 py-3 font-medium text-slate-800 font-mono">{r.attendance_date}</td>
                                <td className="px-4 py-3 text-slate-500 font-mono">
                                  {r.old_in_time || '--:--'} / {r.old_out_time || '--:--'}
                                </td>
                                <td className="px-4 py-3 text-slate-800 font-mono font-semibold">
                                  {r.proposed_in_time || '--:--'} / {r.proposed_out_time || '--:--'}
                                </td>
                                <td className="px-4 py-3 text-slate-600 max-w-[150px] truncate" title={r.reason}>{r.reason}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase ${statusColor(r.status)}`}>
                                    {r.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <div className="flex justify-center gap-2">
                                    <button 
                                      onClick={() => { setPreviewItem(r); setPreviewType('reg'); }}
                                      className="p-1 hover:bg-slate-100 rounded text-slate-600"
                                      title="Preview Workflow"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                    {r.status === 'pending' && (
                                      <button 
                                        onClick={() => handleRegCancel(r.id)}
                                        className="p-1 hover:bg-red-50 rounded text-red-600"
                                        title="Cancel Request"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          {regularizations.length === 0 && (
                            <tr>
                              <td colSpan={7} className="text-center text-slate-400 py-10 font-medium">
                                No regularization requests filed yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* 4. SHIFT REQUESTS TAB REDESIGN */}
            {activeTab === 'shift' && (
              <div className="space-y-6">
                
                {/* Shift Stats Card */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Swaps</p>
                      <p className="text-xl font-bold text-amber-600 mt-1 font-mono">{pendingShiftCount}</p>
                    </div>
                    <ArrowLeftRight className="w-6 h-6 text-amber-200" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Approved Exchanges</p>
                      <p className="text-xl font-bold text-emerald-600 mt-1 font-mono">{approvedShiftCount}</p>
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-emerald-200" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rejected Requests</p>
                      <p className="text-xl font-bold text-red-600 mt-1 font-mono">{rejectedShiftCount}</p>
                    </div>
                    <X className="w-6 h-6 text-red-200" />
                  </div>
                </div>

                {/* Shift History Grid */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800 text-sm">Shift Swap & Exchange Ledger</h3>
                    <button 
                      onClick={() => handleExportExcel('Shift')}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Export Ledger</span>
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Request No</th>
                          <th className="px-4 py-3 font-semibold">Type</th>
                          <th className="px-4 py-3 font-semibold">Current Shift</th>
                          <th className="px-4 py-3 font-semibold">Requested Shift</th>
                          <th className="px-4 py-3 font-semibold">Effective Date</th>
                          <th className="px-4 py-3 font-semibold">Reason</th>
                          <th className="px-4 py-3 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {shiftRequests.map((s, i) => (
                          <tr key={s.id || i} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 font-mono font-medium text-slate-600">S-{String(i + 1).padStart(4, '0')}</td>
                            <td className="px-4 py-3 font-medium capitalize text-slate-800">{s.request_type || 'Change'}</td>
                            <td className="px-4 py-3 text-slate-500 font-mono">{s.current_shift_id || '9 AM - 6 PM'}</td>
                            <td className="px-4 py-3 text-slate-800 font-semibold font-mono">{s.requested_shift_id || '6 AM - 2 PM'}</td>
                            <td className="px-4 py-3 text-slate-600 font-mono">{s.requested_date}</td>
                            <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate" title={s.reason}>{s.reason || 'Personal request'}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase ${statusColor(s.status)}`}>
                                {s.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {shiftRequests.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-slate-400 py-10 font-medium">
                              No shift change requests found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}

            {/* 5. COMP-OFF TAB REDESIGN */}
            {activeTab === 'compoff' && (
              <div className="space-y-6">
                
                {/* Comp-Off Dashboard Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Available Days</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1 font-mono">{availableCompoff}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Used Comp-Offs</p>
                    <p className="text-2xl font-bold text-indigo-600 mt-1 font-mono">{usedCompoff}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Approvals</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1 font-mono">{pendingCompoff}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Expired Days</p>
                    <p className="text-2xl font-bold text-slate-400 mt-1 font-mono">0</p>
                  </div>
                </div>

                {/* Ledger & History */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800 text-sm">Comp-Off Ledger</h3>
                    <button 
                      onClick={() => handleExportExcel('Comp-Off')}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Export ledger</span>
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Request No</th>
                          <th className="px-4 py-3 font-semibold">Worked Date</th>
                          <th className="px-4 py-3 font-semibold">Comp-Off Date</th>
                          <th className="px-4 py-3 font-semibold text-center">Hours Worked</th>
                          <th className="px-4 py-3 font-semibold">Reason</th>
                          <th className="px-4 py-3 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {compoffRequests.map((c, i) => (
                          <tr key={c.id || i} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 font-mono font-medium text-slate-600">C-{String(i + 1).padStart(4, '0')}</td>
                            <td className="px-4 py-3 font-mono text-slate-800">{c.worked_date}</td>
                            <td className="px-4 py-3 font-mono font-semibold text-slate-900">{c.comp_off_date}</td>
                            <td className="px-4 py-3 text-center text-slate-700 font-semibold">{c.hours_worked}h</td>
                            <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate" title={c.reason}>{c.reason}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase ${statusColor(c.status)}`}>
                                {c.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {compoffRequests.length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center text-slate-400 py-10 font-medium">
                              No comp-off requests found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}
          </>
        )}

        {/* ================= MODAL WORKFLOWS ================= */}

        {/* 1. APPLY LEAVE WIZARD MODAL */}
        {showLeaveModal && (
          <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-50 p-4" onClick={() => setShowLeaveModal(false)}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              
              {/* Wizard Title & Step Indicators */}
              <div className="p-5 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-slate-900">Apply Leave Application</h3>
                  <button onClick={() => setShowLeaveModal(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
                </div>
                
                {/* Step indicator bubbles */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                        leaveWizardStep === step 
                          ? 'bg-[#0052FF] text-white ring-4 ring-[#0052FF]/20' 
                          : leaveWizardStep > step 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-slate-200 text-slate-500'
                      }`}>
                        {leaveWizardStep > step ? <Check className="w-4 h-4" /> : step}
                      </div>
                      {step < 3 && <div className={`w-16 h-1 rounded transition-colors ${leaveWizardStep > step ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Wizard Contents */}
              <form id="apply-leave-form" onSubmit={handleLeaveSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                
                {/* STEP 1: Date & Type */}
                {leaveWizardStep === 1 && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Leave Type *</label>
                      <select 
                        data-testid="leave-type-select" 
                        value={leaveForm.leave_type_id} 
                        onChange={(e) => setLeaveForm({ ...leaveForm, leave_type_id: e.target.value })} 
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20 focus:border-[#0052FF]" 
                        required
                      >
                        <option value="">Select a Leave Type</option>
                        {leaveTypes.map((lt) => (
                          <option key={lt.id} value={lt.id}>{lt.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">From Date *</label>
                        <input 
                          type="date" 
                          data-testid="leave-from" 
                          value={leaveForm.from_date} 
                          onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })} 
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                          required 
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">To Date *</label>
                        <input 
                          type="date" 
                          data-testid="leave-to" 
                          value={leaveForm.to_date} 
                          onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} 
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                          required 
                        />
                      </div>
                    </div>

                    <label className="flex items-center gap-2.5 py-1 text-slate-700 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={leaveForm.half_day} 
                        onChange={(e) => setLeaveForm({ ...leaveForm, half_day: e.target.checked })} 
                        className="w-4 h-4 text-[#0052FF] rounded border-slate-300 focus:ring-[#0052FF]/20"
                      />
                      <span className="text-sm font-medium">Apply as Half Day</span>
                    </label>
                  </div>
                )}

                {/* STEP 2: Reason & Details */}
                {leaveWizardStep === 2 && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Reason for Leave *</label>
                      <textarea 
                        data-testid="leave-reason" 
                        value={leaveForm.reason} 
                        onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} 
                        rows={3} 
                        placeholder="Please elaborate your reason..."
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Emergency Contact</label>
                        <input 
                          type="tel" 
                          placeholder="Phone number"
                          value={leaveForm.emergency_contact} 
                          onChange={(e) => setLeaveForm({ ...leaveForm, emergency_contact: e.target.value })} 
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Delegate Tasks To</label>
                        <input 
                          type="text" 
                          placeholder="Employee Name"
                          value={leaveForm.delegate_employee} 
                          onChange={(e) => setLeaveForm({ ...leaveForm, delegate_employee: e.target.value })} 
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Supporting Document (optional)</label>
                      <input 
                        type="file" 
                        onChange={(e) => setLeaveForm({ ...leaveForm, attachment_name: e.target.files[0]?.name || '' })} 
                        className="w-full text-xs text-slate-500 border border-slate-300 rounded-lg px-3 py-2 cursor-pointer bg-slate-50"
                      />
                    </div>
                  </div>
                )}

                {/* STEP 3: Preview & Submit */}
                {leaveWizardStep === 3 && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300 bg-slate-50 p-4 rounded-xl border border-slate-100 text-xs">
                    <h4 className="font-bold text-sm text-slate-900 border-b border-slate-200 pb-2">Leave Summary Details</h4>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-2">
                      <p className="text-slate-500">Leave Type</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveTypes.find(t => t.id === leaveForm.leave_type_id)?.name || 'N/A'}</p>
                      
                      <p className="text-slate-500">Duration</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveForm.from_date} to {leaveForm.to_date}</p>
                      
                      <p className="text-slate-500">Half Day</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveForm.half_day ? 'Yes' : 'No'}</p>

                      <p className="text-slate-500">Reason</p>
                      <p className="font-semibold text-slate-800 text-right truncate max-w-[200px]">{leaveForm.reason || 'N/A'}</p>

                      <p className="text-slate-500">Delegate Task</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveForm.delegate_employee || 'N/A'}</p>

                      <p className="text-slate-500">Emergency Contact</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveForm.emergency_contact || 'N/A'}</p>
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-[10px]">
                      Your request will be routed to your Reporting Manager, then to HR. Click Submit to execute.
                    </div>
                  </div>
                )}

              </form>

              {/* Wizard Footer Actions */}
              <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
                {leaveWizardStep > 1 ? (
                  <button 
                    type="button" 
                    onClick={() => setLeaveWizardStep(prev => prev - 1)}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50"
                  >
                    Back
                  </button>
                ) : (
                  <div />
                )}

                {leaveWizardStep < 3 ? (
                  <button 
                    type="button" 
                    onClick={() => {
                      if (leaveWizardStep === 1 && !leaveForm.leave_type_id) {
                        toast.error('Please pick a leave type first');
                        return;
                      }
                      setLeaveWizardStep(prev => prev + 1);
                    }}
                    className="px-5 py-2.5 bg-[#0052FF] text-white text-xs font-semibold rounded-lg hover:bg-[#0040CC]"
                  >
                    Next
                  </button>
                ) : (
                  <button 
                    form="apply-leave-form"
                    data-testid="submit-leave-btn"
                    type="submit" 
                    onClick={handleLeaveSubmit}
                    disabled={submitting}
                    className="px-6 py-2.5 bg-[#0052FF] text-white text-xs font-semibold rounded-lg hover:bg-[#0040CC] disabled:opacity-50"
                  >
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 2. REGULARIZATION REQUEST MODAL */}
        {showRegModal && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => setShowRegModal(false)}>
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">Attendance Regularization</h3>
                <button onClick={() => setShowRegModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <form onSubmit={handleRegSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Select Attendance Date *</label>
                  {eligibleDates.length > 0 ? (
                    <select
                      data-testid="reg-date-select"
                      value={regForm.attendance_date}
                      onChange={(e) => setRegForm({ ...regForm, attendance_date: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                      required
                    >
                      <option value="">Pick a day needing adjustment</option>
                      {eligibleDates.map((d) => (
                        <option key={d.attendance_date} value={d.attendance_date}>
                          {`${d.attendance_date} — ${d.current_status}`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="date"
                      data-testid="reg-date-input"
                      value={regForm.attendance_date}
                      max={new Date().toISOString().slice(0, 10)}
                      min={new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10)}
                      onChange={(e) => setRegForm({ ...regForm, attendance_date: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                      required
                    />
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Reason / Request Type *</label>
                  <select
                    data-testid="reg-type-select"
                    value={regForm.request_type}
                    onChange={(e) => setRegForm({ ...regForm, request_type: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                  >
                    <option value="missing_punch">Missing punch (forgot to scan)</option>
                    <option value="wrong_punch">Wrong punch (recorded incorrectly)</option>
                    <option value="on_duty">On duty (client visit / outside office)</option>
                    <option value="work_from_home">Work from home</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Proposed In Time</label>
                    <input
                      type="time"
                      data-testid="reg-in-time"
                      value={regForm.proposed_in_time}
                      onChange={(e) => setRegForm({ ...regForm, proposed_in_time: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Proposed Out Time</label>
                    <input
                      type="time"
                      data-testid="reg-out-time"
                      value={regForm.proposed_out_time}
                      onChange={(e) => setRegForm({ ...regForm, proposed_out_time: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Mark Attendance As</label>
                  <select
                    data-testid="reg-status-select"
                    value={regForm.alternate_status}
                    onChange={(e) => setRegForm({ ...regForm, alternate_status: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                  >
                    <option value="">Keep current status</option>
                    <option value="present">Present</option>
                    <option value="half_day">Half Day</option>
                    <option value="on_duty">On Duty</option>
                    <option value="wfh">Work From Home</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Detailed Explanation *</label>
                  <textarea
                    data-testid="reg-reason"
                    value={regForm.reason}
                    onChange={(e) => setRegForm({ ...regForm, reason: e.target.value })}
                    rows={3}
                    placeholder="Provide details for punch correction..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20"
                    required
                  />
                </div>

                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-blue-800 text-[10px] leading-relaxed">
                  Regularization requests are processed by your Reporting Manager and HR. Alternate status is updated on approval.
                </div>

                <button
                  data-testid="submit-reg-btn"
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* 3. SHIFT REQUEST MODAL */}
        {showShiftModal && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => setShowShiftModal(false)}>
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">Shift Swap & Change</h3>
                <button onClick={() => setShowShiftModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <form onSubmit={handleShiftSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Exchange Type</label>
                  <select 
                    value={shiftForm.request_type} 
                    onChange={(e) => setShiftForm({ ...shiftForm, request_type: e.target.value })} 
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                  >
                    <option value="change">Shift Change</option>
                    <option value="swap">Shift Swap (with peer)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Effective Date *</label>
                  <input 
                    type="date" 
                    value={shiftForm.requested_date} 
                    onChange={(e) => setShiftForm({ ...shiftForm, requested_date: e.target.value })} 
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                    required 
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Current Shift</label>
                    <input 
                      type="text" 
                      value={shiftForm.current_shift_id} 
                      disabled 
                      className="w-full border border-slate-200 bg-slate-50 text-slate-400 rounded-lg px-3 py-2.5 text-sm" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Requested Shift *</label>
                    <select 
                      value={shiftForm.requested_shift_id} 
                      onChange={(e) => setShiftForm({ ...shiftForm, requested_shift_id: e.target.value })} 
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                    >
                      <option value="MOR_6_2">Morning Shift (6 AM - 2 PM)</option>
                      <option value="GEN_9_6">General Shift (9 AM - 6 PM)</option>
                      <option value="AFT_2_10">Afternoon Shift (2 PM - 10 PM)</option>
                      <option value="NIGHT_10_6">Night Shift (10 PM - 6 AM)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Comments / Reason *</label>
                  <textarea 
                    value={shiftForm.reason} 
                    onChange={(e) => setShiftForm({ ...shiftForm, reason: e.target.value })} 
                    rows={2} 
                    placeholder="Enter reason for shift exchange..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                    required 
                  />
                </div>

                <button 
                  data-testid="submit-shift-btn" 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] disabled:opacity-50 transition-all"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* 4. COMP-OFF REQUEST MODAL */}
        {showCompoffModal && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => setShowCompoffModal(false)}>
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">Request Comp-Off Day</h3>
                <button onClick={() => setShowCompoffModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <form onSubmit={handleCompoffSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Worked Date *</label>
                    <input 
                      type="date" 
                      value={compoffForm.worked_date} 
                      onChange={(e) => setCompoffForm({ ...compoffForm, worked_date: e.target.value })} 
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                      required 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Comp-Off Date *</label>
                    <input 
                      type="date" 
                      value={compoffForm.comp_off_date} 
                      onChange={(e) => setCompoffForm({ ...compoffForm, comp_off_date: e.target.value })} 
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                      required 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Hours Worked</label>
                  <input 
                    type="number" 
                    value={compoffForm.hours_worked} 
                    onChange={(e) => setCompoffForm({ ...compoffForm, hours_worked: parseFloat(e.target.value) })} 
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Reason *</label>
                  <textarea 
                    value={compoffForm.reason} 
                    onChange={(e) => setCompoffForm({ ...compoffForm, reason: e.target.value })} 
                    rows={2} 
                    placeholder="Mention project name or work detail..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                    required 
                  />
                </div>

                <button 
                  data-testid="submit-compoff-btn" 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] disabled:opacity-50 transition-all"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* 5. PREVIEW WORKFLOW DRAWER / MODAL */}
        {previewItem && (
          <div className="fixed inset-0 bg-black/55 z-50 flex items-center justify-end" onClick={() => setPreviewItem(null)}>
            <div className="bg-white h-full w-full max-w-md shadow-2xl p-6 overflow-y-auto flex flex-col justify-between" onClick={e => e.stopPropagation()}>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Application Preview</h3>
                    <p className="text-xs text-slate-500 font-mono mt-0.5">Type: {previewType?.toUpperCase()}</p>
                  </div>
                  <button onClick={() => setPreviewItem(null)} className="p-1 hover:bg-slate-100 rounded">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                {/* Workflow Timeline */}
                <div className="space-y-4">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Approval Workflow Timeline</h4>
                  <div className="relative border-l-2 border-slate-200 pl-6 ml-3 space-y-6 py-2">
                    
                    {/* Step 1: Submission */}
                    <div className="relative">
                      <div className="absolute -left-[31px] top-0.5 bg-emerald-500 text-white rounded-full p-1">
                        <Check className="w-3 h-3" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Submitted by Employee</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 font-mono">
                          {previewItem.created_at ? previewItem.created_at.slice(0, 16).replace('T', ' ') : 'Just now'}
                        </p>
                      </div>
                    </div>

                    {/* Step 2: Reporting Manager */}
                    <div className="relative">
                      <div className={`absolute -left-[31px] top-0.5 rounded-full p-1 ${
                        ['approved', 'rejected', 'cancelled'].includes(previewItem.status)
                          ? 'bg-emerald-500 text-white'
                          : 'bg-amber-500 text-white animate-pulse'
                      }`}>
                        {['approved', 'rejected', 'cancelled'].includes(previewItem.status) ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Reporting Manager Review</p>
                        <p className="text-[10px] text-slate-500 font-medium">Stage: Manager Verification</p>
                        {previewItem.status === 'rejected' && <p className="text-[10px] text-red-600 font-medium mt-1">Rejected remarks: "{previewItem.approver_remarks || 'Not approved'}"</p>}
                      </div>
                    </div>

                    {/* Step 3: HR stage */}
                    <div className="relative">
                      <div className={`absolute -left-[31px] top-0.5 rounded-full p-1 ${
                        previewItem.status === 'approved'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-200 text-slate-400'
                      }`}>
                        {previewItem.status === 'approved' ? <Check className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">HR final approval</p>
                        <p className="text-[10px] text-slate-500 font-medium">Stage: Final Release</p>
                      </div>
                    </div>

                    {/* Step 4: Approved/Final status */}
                    <div className="relative">
                      <div className={`absolute -left-[31px] top-0.5 rounded-full p-1 ${
                        previewItem.status === 'approved'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-200 text-slate-400'
                      }`}>
                        {previewItem.status === 'approved' ? <Check className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Status Resolved</p>
                        <p className="text-[10px] text-slate-500">Current state: <strong className="uppercase">{previewItem.status}</strong></p>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Details list */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-xs space-y-2.5">
                  <h4 className="font-bold text-slate-800">Application Details</h4>
                  
                  {previewType === 'leave' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Leave Type</span>
                        <span className="font-semibold text-slate-800">{previewItem.leave_type_name || previewItem.leave_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Duration</span>
                        <span className="font-semibold text-slate-800">{previewItem.from_date} to {previewItem.to_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Total Days</span>
                        <span className="font-semibold text-slate-800">{previewItem.days || 1} day(s)</span>
                      </div>
                    </>
                  )}

                  {previewType === 'reg' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Date</span>
                        <span className="font-semibold text-slate-800 font-mono">{previewItem.attendance_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Proposed In/Out</span>
                        <span className="font-semibold text-slate-800 font-mono">{previewItem.proposed_in_time} / {previewItem.proposed_out_time}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between">
                    <span className="text-slate-500">Reason</span>
                    <span className="font-semibold text-slate-800 truncate max-w-[200px]" title={previewItem.reason}>{previewItem.reason || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setPreviewItem(null)} 
                className="w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold mt-6 transition-all"
              >
                Close Preview
              </button>

            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default ESSLeaveAttendance;
